/**
 * Our own paperwork, from its row: the W-9 or the certificate of insurance.
 * Upload it once, then email it to a customer.
 *
 * The document is StandingDocs' own, the same for every customer, so the
 * upload says so plainly — replacing it here replaces it everywhere. Sending
 * attaches the PDF to an email to the chosen person (their billing contact by
 * default), with replies going to the company, and marks the row sent. The
 * customer's onboarding form also offers it as a download.
 *
 * A certificate of insurance expires: it is uploaded with that date, the row
 * warns when it has passed, and an expired one cannot be sent.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import { formatDayUS } from '../../../shared/dates';
import { EMAIL_MAX } from '../../../types/fieldLimits';
import page from '../../../shared/Page.module.css';
import form from '../../../shared/Form.module.css';
import type { Person } from './types';
import { primaryFor } from './people';

const OTHER = '__other__';
const MAX_BYTES = 5 * 1024 * 1024;

export type CompanyDocKind = 'w9' | 'coi';

const SPEC: Record<CompanyDocKind, { label: string; guide: string; short: string }> = {
  w9: { label: 'W-9', short: 'W-9', guide: 'Fill in and sign the IRS form once (see docs/contracts/w9-guidance.md)' },
  coi: {
    label: 'certificate of insurance', short: 'certificate',
    guide: 'Ask your insurance broker for the certificate (see docs/contracts/insurance-requirements.md)',
  },
};

interface DocStatus {
  stored: boolean; fileName?: string; uploadedAt?: string; uploadedByEmail?: string | null; url?: string;
  validUntil?: string | null; expired?: boolean;
}

/** Base64 of a file, without the "data:...;base64," prefix. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export function CompanyDocControls({ kind, orgId, people }: { kind: CompanyDocKind; orgId: string; people: Person[] }) {
  const spec = SPEC[kind];
  const expires = kind === 'coi';
  const [validUntil, setValidUntil] = useState('');
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const billing = primaryFor(people, 'billing');
  const [choice, setChoice] = useState(billing?.email ?? people[0]?.email ?? OTHER);
  const [other, setOther] = useState('');
  const to = choice === OTHER ? other.trim().toLowerCase() : choice;
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to);

  const status = useQuery({
    queryKey: ['companyDoc', kind],
    queryFn: () => call<{ kind: CompanyDocKind }, DocStatus>('sysAdminGetCompanyDoc', { kind }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BYTES) throw new Error('That file is over 5 MB.');
      return call('sysAdminUploadCompanyDoc', {
        kind, fileName: file.name, base64: await toBase64(file), ...(expires ? { validUntil } : {}),
      });
    },
    onSuccess: () => {
      setNotice(`Uploaded. It is the ${spec.label} every customer now receives.`);
      setValidUntil('');
      void queryClient.invalidateQueries({ queryKey: ['companyDoc', kind] });
    },
  });

  const view = useMutation({
    mutationFn: async () => {
      const tab = window.open('', '_blank');
      try {
        const r = await call<{ kind: CompanyDocKind; withLink: boolean }, DocStatus>('sysAdminGetCompanyDoc', { kind, withLink: true });
        if (r.url && tab) tab.location.href = r.url; else tab?.close();
      } catch (e) { tab?.close(); throw e; }
    },
  });

  const send = useMutation({
    mutationFn: () => call<{ kind: CompanyDocKind; orgId: string; to: string }, { to: string }>('sysAdminSendCompanyDoc', { kind, orgId, to }),
    onSuccess: (r) => {
      setNotice(`Emailed to ${r.to}, with the ${spec.label} attached. Replies go to the company address.`);
      void queryClient.invalidateQueries({ queryKey: ['onboarding', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', orgId] });
    },
  });

  const error = problem ?? [upload.error, view.error, send.error, status.error]
    .map((e) => (e ? (isCallFailure(e) ? e.message : e instanceof Error ? e.message : 'That did not work.') : null))
    .find(Boolean);
  const busy = upload.isPending || send.isPending;
  const doc = status.data;
  const needsDate = expires && !/^\d{4}-\d{2}-\d{2}$/.test(validUntil);
  const chooseFile = () => { if (needsDate) setProblem(`Enter the date the ${spec.label} expires first.`); else fileRef.current?.click(); };
  const dateField = expires && (
    <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center', margin: '0 var(--sp-2)' }}>
      <label htmlFor={`${kind}-valid-until`} className={page.itemMeta}>Expires on</label>
      <input id={`${kind}-valid-until`} type="date" className={form.input} style={{ width: 160 }}
        value={validUntil} onChange={(e) => { setValidUntil(e.target.value); setProblem(null); }} />
    </span>
  );

  return (
    <div style={{ flexBasis: '100%', marginTop: 'var(--sp-2)' }}>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden aria-label={`Our ${spec.label} (PDF)`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          setProblem(null); setNotice(null);
          if (f) upload.mutate(f);
        }} />

      {status.isLoading ? null : !doc?.stored ? (
        <p className={page.itemMeta}>
          No {spec.label} uploaded yet. {spec.guide}, save it as a PDF, and
          {dateField || ' '}
          <button type="button" className={page.linkBtn} disabled={busy} onClick={chooseFile}>
            {upload.isPending ? 'uploading…' : 'upload it'}
          </button>
          . It is StandingDocs&apos; own, used for every customer.
        </p>
      ) : (
        <>
          <p className={page.itemMeta}>
            Our {spec.short}: {doc.fileName}, uploaded {formatDayUS(doc.uploadedAt ?? null)}
            {doc.uploadedByEmail ? ` by ${doc.uploadedByEmail}` : ''}.
            {doc.validUntil && (
              <span className={doc.expired ? page.dangerText : undefined}>
                {' '}{doc.expired ? `Expired ${formatDayUS(doc.validUntil)}: upload the renewed one before sending it.` : `Valid until ${formatDayUS(doc.validUntil)}.`}
              </span>
            )}{' '}
            <button type="button" className={page.linkBtn} disabled={view.isPending} onClick={() => view.mutate()}>View</button>
            {' · '}
            {dateField}
            <button type="button" className={page.linkBtn} disabled={busy} onClick={chooseFile}>
              {upload.isPending ? 'Uploading…' : 'Replace for every customer'}
            </button>
          </p>
          <div className={form.row} style={{ alignItems: 'end', marginTop: 'var(--sp-2)' }}>
            <div>
              <label className={form.label} htmlFor={`${kind}-to`}>Email it to</label>
              <select id={`${kind}-to`} className={form.select} value={choice} onChange={(e) => setChoice(e.target.value)}>
                {people.map((p) => (
                  <option key={p.id} value={p.email}>
                    {p.name} · {p.email}{p.roles.includes('billing') ? ' (billing)' : ''}
                  </option>
                ))}
                <option value={OTHER}>Another address</option>
              </select>
            </div>
            {choice === OTHER && (
              <div>
                <label className={form.label} htmlFor={`${kind}-other`}>Email address</label>
                <input id={`${kind}-other`} type="email" className={form.input} maxLength={EMAIL_MAX} placeholder="name@hospital.org"
                  value={other} onChange={(e) => setOther(e.target.value)} />
              </div>
            )}
            <div>
              <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={!valid || busy || !!doc.expired}
                onClick={() => { setNotice(null); send.mutate(); }}>
                {send.isPending ? 'Sending…' : `Send ${spec.short}`}
              </button>
            </div>
          </div>
        </>
      )}

      {error && <div className={`${page.notice} ${page.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-2)' }}>{error}</div>}
      {notice && <div className={`${page.notice} ${page.noticeInfo}`} role="status" style={{ marginTop: 'var(--sp-2)' }}>{notice}</div>}
    </div>
  );
}
